import { InfoCircleOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { useContext } from 'react';

import {
  DwollaBank,
  PortalAutopaySettingsFragment,
  PortalBorrowerAchAccountFragment,
  PortalLoan,
  RepayAccount,
} from '@willow/graphql-iso/src/portal';
import { Form } from '@willow/shared-web/bootstrap';
import { BorrowerId, LoanId, LoanStatus, PortalUserId } from '@willow/types-iso';

import { AutopayContext } from '../../../contexts/autopayContext';
import { FeatureFlagContext } from '../../../contexts/featureFlagContext';
import { ConfirmDwollaAutopayModal } from '../../autopay/ConfirmDwollaAutopayModal';
import { ConfirmNachaAutopayModal } from '../../autopay/ConfirmNachaAutopayModal';
import { ConfirmRepayAutopayModal } from '../../autopay/ConfirmRepayAutopayModal';
import { DisableAutopayModal } from '../../autopay/DisableAutopayModal';
import { UserContext } from '../../context/UserContext';
import { isBankAccountLocked } from '../helpers';

interface Props {
  autopaySettings?: PortalAutopaySettingsFragment | null;
  paymentBank?: DwollaBank | RepayAccount | PortalBorrowerAchAccountFragment;
  loanStatus: LoanStatus;
  loanId: LoanId;
  totalAmountDue: number;
  isPastDue?: boolean;
  repayAccounts: PortalLoan['repayAccounts'];
  repayEnabled: boolean;
  achAccounts: PortalBorrowerAchAccountFragment[];
  nachaEnabled: boolean;
}

function getAutopaySettingsStatus(
  autopaySettings: PortalAutopaySettingsFragment | null | undefined,
  paymentBank: DwollaBank | RepayAccount | PortalBorrowerAchAccountFragment | undefined,
  portalUserId: PortalUserId | undefined,
  borrowerId: BorrowerId | undefined,
  loanStatus: LoanStatus,
) {
  let autopayEnabledByCoBorrower = false;
  let autopayEnabledByServicer = false;

  if (autopaySettings?.actor) {
    if (autopaySettings.actor.type === 'borrower') {
      autopayEnabledByCoBorrower = autopaySettings.actor.id !== borrowerId;
    } else if (autopaySettings.actor.type === 'user') {
      autopayEnabledByServicer = true;
    }
  } else {
    // TODO Remove once we are migrated to the new actor model
    autopayEnabledByCoBorrower = Boolean(
      autopaySettings && 'portalUserId' in autopaySettings && autopaySettings.portalUserId !== portalUserId,
    );
    autopayEnabledByServicer = Boolean(autopaySettings && autopaySettings.type === 'usio');
  }

  const hasUsableBank = Boolean(paymentBank) && !isBankAccountLocked(paymentBank);

  return {
    // Allow missing/locked bank accounts to disable autopay, but not enable it.
    autopayToggleIsLocked:
      autopayEnabledByCoBorrower ||
      autopayEnabledByServicer ||
      (!autopaySettings && !hasUsableBank) ||
      ['transferred', 'archived'].includes(loanStatus),
    autopayEnabledByCoBorrower,
    autopayEnabledByServicer,
    hasUsableBank,
  };
}

export const AutopaySettingForm = (props: Props) => {
  const {
    autopaySettings,
    paymentBank,
    loanStatus,
    loanId,
    totalAmountDue,
    isPastDue,
    repayAccounts,
    repayEnabled,
    achAccounts,
    nachaEnabled,
  } = props;

  const { setShowConfirmAutopayModal, setShowDisableAutopayModal } = useContext(AutopayContext);

  const { ff_autopaySettings } = useContext(FeatureFlagContext);
  const { id: portalUserId, borrowerId } = useContext(UserContext);

  const { autopayToggleIsLocked, autopayEnabledByCoBorrower, autopayEnabledByServicer, hasUsableBank } =
    getAutopaySettingsStatus(autopaySettings, paymentBank, portalUserId, borrowerId, loanStatus);

  const autopayToggleDisplay = Boolean(ff_autopaySettings && !['transferred', 'archived'].includes(loanStatus));

  const onToggleAutopay = () => {
    if (autopaySettings) {
      setShowDisableAutopayModal(true);
    } else {
      setShowConfirmAutopayModal(true);
    }
  };

  return (
    autopayToggleDisplay && (
      <>
        <h2>Enable auto-pay</h2>
        {autopayEnabledByCoBorrower && (
          <div className="payment-form__autopay-callout">
            <InfoCircleOutlined />A co-borrower currently has auto-pay enabled on this loan.
          </div>
        )}
        {autopayEnabledByServicer && (
          <div className="payment-form__autopay-callout">
            <InfoCircleOutlined />
            Your servicer has enabled auto-pay on this loan. If you believe this is an error, please contact your
            servicer immediately.
          </div>
        )}
        <div
          className={classNames('d-flex gap-3 payment-form__autopay', {
            'payment-form__autopay--enabled': !!autopaySettings,
            'payment-form__autopay--not-allowed': autopayToggleIsLocked,
          })}
        >
          <Form.Check
            type="switch"
            checked={!!autopaySettings}
            onChange={onToggleAutopay}
            disabled={autopayToggleIsLocked}
          />
          {/* Display different copy depending on reason for being disabled */}
          {autopaySettings || hasUsableBank ? (
            <>Automatically submit balance due on due date of each month</>
          ) : (
            <>Connect your bank account above to enable auto-pay</>
          )}
        </div>
        {nachaEnabled ? (
          <ConfirmNachaAutopayModal
            loanId={loanId}
            pastDueBalance={isPastDue ? totalAmountDue : undefined}
            achAccounts={achAccounts}
          />
        ) : repayEnabled ? (
          <ConfirmRepayAutopayModal
            loanId={loanId}
            pastDueBalance={isPastDue ? totalAmountDue : undefined}
            repayAccounts={repayAccounts}
          />
        ) : (
          <ConfirmDwollaAutopayModal loanId={loanId} pastDueBalance={isPastDue ? totalAmountDue : undefined} />
        )}
        {autopaySettings && autopaySettings.type !== 'usio' && (
          <DisableAutopayModal loanId={loanId} paymentProvider={autopaySettings.type} />
        )}
      </>
    )
  );
};
